/**
 * Authentication provider.
 *
 * Supports both modes the API supports, chosen at runtime from /api/auth/config
 * so the built bundle is the same in development and production:
 *
 * - `entra` — MSAL browser with the PKCE redirect flow against Entra External
 *   ID. Access tokens are acquired silently and refreshed by MSAL.
 * - `dev`   — a local token from /api/auth/dev-login, so the site is fully
 *   usable before an External ID tenant exists.
 *
 * MSAL is imported dynamically so its ~200 KB only loads for shops actually
 * using Entra, and never for a guest browsing the catalogue.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthPrincipal } from '@gills/shared';
import { api, setTokenProvider } from './api';

type AuthConfig = Awaited<ReturnType<typeof api.authConfig>>;

interface AuthContextValue {
  principal: AuthPrincipal | null;
  /** Why a presented token was refused, when one was. */
  tokenError: string | null;
  mode: 'dev' | 'entra' | 'unconfigured' | null;
  ready: boolean;
  signingIn: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Dev mode only — sign in as an arbitrary local identity. */
  devSignIn: (email: string, name: string, isAdmin: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const DEV_TOKEN_KEY = 'gills.devToken.v1';

/** Minimal shape of the MSAL pieces we use, so this file needs no static import. */
type Msal = typeof import('@azure/msal-browser');
type MsalApp = InstanceType<Msal['PublicClientApplication']>;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [principal, setPrincipal] = useState<AuthPrincipal | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [msalApp, setMsalApp] = useState<MsalApp | null>(null);
  const [ready, setReady] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  // -- Boot ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      let loaded: AuthConfig;
      try {
        loaded = await api.authConfig();
      } catch {
        // The shop must stay usable for guests even if auth config fails.
        if (!cancelled) setReady(true);
        return;
      }
      if (cancelled) return;
      setConfig(loaded);

      // Deployed before the Entra tenant exists. Everything except sign-in
      // works, so come up ready rather than blocking the storefront.
      if (loaded.mode === 'unconfigured') {
        if (!cancelled) setReady(true);
        return;
      }

      if (loaded.mode === 'dev') {
        const token = localStorage.getItem(DEV_TOKEN_KEY);
        if (token) {
          setTokenProvider(async () => localStorage.getItem(DEV_TOKEN_KEY));
          const me = await api.me().catch(() => ({ principal: null }));
          if (!cancelled) {
            if (me.principal) {
              setPrincipal(me.principal);
            } else {
              // Token expired or the signing secret changed — clear it rather
              // than leaving a dead Authorization header on every request.
              localStorage.removeItem(DEV_TOKEN_KEY);
              setTokenProvider(null);
            }
          }
        }
        if (!cancelled) setReady(true);
        return;
      }

      // Entra External ID
      const msal = await import('@azure/msal-browser');
      if (cancelled) return;

      const instance = new msal.PublicClientApplication({
        auth: {
          clientId: loaded.clientId,
          authority: loaded.authority,
          knownAuthorities: loaded.knownAuthorities,
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
          navigateToLoginRequestUrl: true,
        },
        cache: {
          // sessionStorage keeps a token out of a shared machine's persistent
          // storage; MSAL refreshes silently within the session.
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        },
      });

      await instance.initialize();
      // Completes the redirect leg of the PKCE flow when we land back here.
      const result = await instance.handleRedirectPromise().catch(() => null);
      if (result?.account) instance.setActiveAccount(result.account);
      if (!instance.getActiveAccount()) {
        const [first] = instance.getAllAccounts();
        if (first) instance.setActiveAccount(first);
      }

      if (cancelled) return;
      setMsalApp(instance);

      setTokenProvider(async () => {
        const account = instance.getActiveAccount();
        if (!account) return null;
        try {
          const token = await instance.acquireTokenSilent({
            scopes: [loaded.apiScope],
            account,
          });
          return token.accessToken;
        } catch {
          // Silent acquisition fails when the session has genuinely expired.
          // Returning null degrades to guest rather than forcing a redirect
          // mid-browse; the next explicit sign-in will re-authenticate.
          return null;
        }
      });

      if (instance.getActiveAccount()) {
        const me = await api
          .me()
          .catch(() => ({ principal: null, tokenError: 'Could not reach the API.' }));
        if (!cancelled) {
          setPrincipal(me.principal);
          setTokenError(me.tokenError ?? null);
        }
      }
      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // -- Actions ---------------------------------------------------------------
  const signIn = useCallback(async () => {
    if (!config) return;
    setSigningIn(true);
    try {
      if (config.mode === 'entra' && msalApp) {
        await msalApp.loginRedirect({ scopes: [config.apiScope] });
      }
      // In dev mode the UI presents devSignIn instead; there is nothing to
      // redirect to.
    } finally {
      setSigningIn(false);
    }
  }, [config, msalApp]);

  const devSignIn = useCallback(async (email: string, name: string, isAdmin: boolean) => {
    setSigningIn(true);
    try {
      const { token, principal: signedIn } = await api.devLogin(email, name, isAdmin);
      localStorage.setItem(DEV_TOKEN_KEY, token);
      setTokenProvider(async () => localStorage.getItem(DEV_TOKEN_KEY));
      setPrincipal(signedIn);
    } finally {
      setSigningIn(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    if (config?.mode === 'entra' && msalApp) {
      setTokenProvider(null);
      setPrincipal(null);
      await msalApp.logoutRedirect();
      return;
    }
    localStorage.removeItem(DEV_TOKEN_KEY);
    setTokenProvider(null);
    setPrincipal(null);
  }, [config, msalApp]);

  const value = useMemo<AuthContextValue>(
    () => ({
      principal,
      tokenError,
      mode: config?.mode ?? null,
      ready,
      signingIn,
      signIn,
      signOut,
      devSignIn,
    }),
    [principal, tokenError, config, ready, signingIn, signIn, signOut, devSignIn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
